/**
 * PublicBookingPage — the native "our Calendly" page at /book/:slug (V1b).
 * Day chips → free slot chips (server-computed) → name/email → booked.
 * Manus-minimal, no app chrome; hidden honeypot; slot list is a suggestion —
 * the server revalidates and answers slot races honestly.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { t } from '../../i18n';
import { useParams } from 'react-router-dom';
import { BOOKING_LOCALE, dayKey, dayLabel, timeLabel, zoneOpt } from './publicBookingTime';
import { browserZone, merchantClock, useViewZone, zoneDisplayName } from './publicBookingZone';
import { isEmbedded, openCheckout, postToParent, useEmbedResize } from './embedBridge';
import PublicBookingLocation, { type PublicLocation } from './PublicBookingLocation';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.buzzbitx.com';

/** One of the merchant's own questions — see server bookingQuestions.ts. */
interface BookingQuestion {
  key: string;
  label: string;
  type: 'text' | 'choice' | 'phone' | 'email';
  required: boolean;
  options?: string[];
}

/** The refusals that are about the booker's phone field, not the slot. */
const PHONE_REFUSALS = new Set(['phone_required', 'bad_phone']);

interface PublicBooking {
  /** V38 — who the booker is booking with (Brand DNA name). Absent on an older server. */
  businessName?: string;
  title: string;
  description: string | null;
  slots: { start: string; end: string }[];
  /** Absent on a page that predates questions, so default to asking none. */
  questions?: BookingQuestion[];
  /**
   * E16 — the merchant's own look. Absent or null on either means the neutral
   * default page, which is what every page rendered before this existed.
   */
  brandLogoUrl?: string | null;
  brandColor?: string | null;
  /** The merchant's IANA zone — slots are SHOWN on it. Null = their fixed offset below. */
  timeZone?: string | null;
  /** The page's UTC offset; the merchant clock when `timeZone` is null. Absent on an older server. */
  tzOffsetMinutes?: number | null;
  /** "Israel Time" / "UTC+03:00" — what the page tells the booker. */
  timeZoneLabel?: string | null;
  /** Where it happens. Absent on an older server = a video call, phone optional. */
  location?: PublicLocation | null;
}

/**
 * The accent colour, re-checked HERE as well as on the server.
 *
 * The server is the boundary and it stores only a hex literal — but this value
 * reaches a `style` on a page served to anonymous visitors, and a second check
 * at the point of use costs nothing. If a row ever carries something else,
 * through a path that bypassed `writableColumns` or a future migration, this
 * renders the neutral default instead of interpolating it.
 */
const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
function safeAccent(value: string | null | undefined): string | null {
  return typeof value === 'string' && HEX.test(value.trim()) ? value.trim() : null;
}

export default function PublicBookingPage(): React.ReactElement {
  const { slug = '' } = useParams();
  const [data, setData] = useState<PublicBooking | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [manageUrl, setManageUrl] = useState<string | null>(null);
  const [website, setWebsite] = useState(''); // honeypot
  /** Replies keyed by question key; validated again server-side. */
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [joinUrl, setJoinUrl] = useState<string | null>(null);
  const accent = safeAccent(data?.brandColor);
  // 3.1 — the merchant's clock by default; the booker may flip to their own.
  // A merchant with no IANA zone is on a fixed offset ("UTC+03:00"), and the
  // slots are drawn on THAT clock so they match the label. Only when no clock
  // can be resolved are they drawn on the browser's — and then named as such.
  const merchantTz = data ? merchantClock(data.timeZone, data.tzOffsetMinutes) : null;
  const zone = useViewZone(merchantTz);
  const tz = zone.shownTz;
  const onViewerClock = zone.showingMine || merchantTz === null;
  const [viewerLabel] = useState(() => { const mine = browserZone(); return mine ? zoneDisplayName(mine) : null; });
  const shownZoneLabel = zone.showingMine
    ? zone.mine?.label ?? null
    : merchantTz === null ? viewerLabel : data?.timeZoneLabel ?? null;
  const phoneRequired = data?.location?.phoneRequired === true;
  // On a merchant's site (public/embed.js): no page chrome, the host iframe
  // follows our height, and a confirmed booking is announced to it.
  const [embedded] = useState(() => isEmbedded());
  useEmbedResize(embedded);

  const load = useCallback(async (preserveDay = false) => {
    try {
      const res = await fetch(`${API_URL}/api/booking-page/public/${slug}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message ?? 'This booking page is unavailable.');
      const parsed = (json?.data ?? json) as PublicBooking;
      setData(parsed);
      // On initial load, select the first day. On reload after an error,
      // preserve the user's selection so they don't lose their place.
      if (!preserveDay) {
        const firstDay = parsed.slots[0] ? dayKey(parsed.slots[0].start, merchantClock(parsed.timeZone, parsed.tzOffsetMinutes)) : null;
        setSelectedDay(firstDay);
      }
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'This booking page is unavailable.');
    }
  }, [slug]);

  useEffect(() => { void load(); }, [load]);

  /** Same slot stays picked; the day chip follows it (or the first slot) onto the new clock. */
  const switchZone = (): void => {
    const next = zone.toggle();
    const anchor = selectedSlot ?? data?.slots[0]?.start;
    setSelectedDay(anchor ? dayKey(anchor, next) : null);
  };

  const days = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of data?.slots ?? []) {
      if (!map.has(dayKey(s.start, tz))) map.set(dayKey(s.start, tz), s.start);
    }
    return Array.from(map.entries());
  }, [data, tz]);

  const daySlots = useMemo(
    () => (data?.slots ?? []).filter((s) => dayKey(s.start, tz) === selectedDay),
    [data, selectedDay, tz],
  );

  const book = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) { setSubmitError('Pick a time first.'); return; }
    setSubmitting(true);
    setSubmitError(null);
    const inviteeZone = browserZone();
    try {
      const res = await fetch(`${API_URL}/api/booking-page/public/${slug}/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          name: name || undefined,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          startsAt: selectedSlot,
          ...(Object.keys(answers).length > 0 ? { answers } : {}),
          website,
          // The booker's own zone, stored on the booking. Omitted when the
          // browser has none; the server stores null for one it does not know.
          ...(inviteeZone ? { timeZone: inviteeZone } : {}),
        }),
      });
      const json = await res.json();
      // A refusal about the phone field keeps the chosen time: the slot is
      // still free, and making the booker pick it again would punish a typo.
      // `field` is read too — `details` objects are stripped in production.
      const code: unknown = json?.error?.details?.code ?? (json?.error?.field === 'phone' ? 'phone_required' : undefined);
      if (!res.ok && typeof code === 'string' && PHONE_REFUSALS.has(code)) {
        setSubmitError(code === 'phone_required' ? t('booking.public.phoneRequired') : json?.error?.message ?? t('booking.public.phoneRequired'));
        return;
      }
      if (!res.ok) {
        // Slot may have been taken while choosing — refresh the list honestly.
        // Preserve the selected day so the booker doesn't jump back to today.
        setSubmitError(json?.error?.message ?? 'Could not book. Please try again.');
        setSelectedSlot(null);
        await load(true);
        return;
      }
      const payload = (json?.data ?? json) as { joinUrl?: string | null; payUrl?: string | null; manageUrl?: string | null };
      /**
       * A deposit-required booking is a HOLD, not a confirmation.
       *
       * Straight to checkout rather than a "booked!" screen with a pay button
       * beside it: the slot is held for a few minutes and then released, so any
       * screen that lets the customer wander off is a screen that loses them
       * the appointment they think they have.
       */
      if (typeof payload?.payUrl === 'string' && payload.payUrl.length > 0) {
        if (embedded) openCheckout(payload.payUrl);
        else window.location.href = payload.payUrl;
        return;
      }
      setJoinUrl(payload?.joinUrl ?? null);
      setManageUrl(payload?.manageUrl ?? null);
      setDone(true);
      if (embedded) postToParent({ type: 'buzzbit:booked' });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Could not book. Please try again.');
    } finally {
      setSubmitting(false);
    }
    // `answers` belongs here. Left out, this callback closes over the EMPTY
    // object it was created with, so every reply the booker typed is dropped
    // at submit while the form still shows it — the request goes out without
    // them and the server rejects a question the booker can see they answered.
  }, [slug, email, name, phone, website, selectedSlot, answers, load, embedded]);

  return (
    <div className={embedded ? 'bg-white p-2' : 'min-h-screen bg-gray-50 px-4 py-12 pb-cookie-banner [--pb-cookie-base:3rem]'}>
      <div className={embedded ? 'mx-auto w-full max-w-lg p-4' : 'mx-auto w-full max-w-lg rounded-2xl border border-gray-200 bg-white p-8 shadow-sm'}>
        {loadError ? (
          <p className="text-center text-sm text-gray-500">{loadError}</p>
        ) : !data ? (
          <p className="text-center text-sm text-gray-400">Loading…</p>
        ) : done ? (
          <div className="py-8 text-center">
            <h1 className="text-xl font-semibold text-gray-900">Booked ✓</h1>
            <p className="mt-2 text-sm text-gray-500">
              {selectedSlot ? new Date(selectedSlot).toLocaleString(BOOKING_LOCALE, { weekday: 'long', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23', ...zoneOpt(tz) }) : ''}
              {shownZoneLabel ? ` (${shownZoneLabel})` : ''}
              {' — '}we'll send you a reminder before it starts.
            </p>
            {joinUrl && (
              <div className="mt-6">
                <a
                  href={joinUrl}
                  className="inline-flex items-center gap-2 rounded-full bg-gray-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
                >
                  Join the video call
                </a>
                <p className="mt-2 text-xs text-gray-400">Save this link — we'll also include it in your reminder.</p>
              </div>
            )}
            {manageUrl && (
              <p className="mt-4 text-xs text-gray-500">
                <a href={manageUrl} className="underline" data-testid="manage-link">{t('booking.public.manage')}</a>
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={book}>
            {/* E16 — the merchant's shopfront. The logo renders only when the
                server stored an https URL, so a refused value leaves the page
                looking exactly as it did before branding existed. */}
            {data.brandLogoUrl && (
              <img
                src={data.brandLogoUrl}
                alt=""
                className="mb-4 h-10 w-auto object-contain"
                data-testid="brand-logo"
              />
            )}
            {data.businessName && (
              <p className="mb-1 text-sm font-medium text-gray-500" data-testid="business-name">{data.businessName}</p>
            )}
            <h1
              className="text-2xl font-bold text-gray-900"
              {...(accent !== null ? { style: { color: accent } } : {})}
              data-testid="brand-title"
            >
              {data.title}
            </h1>
            {data.description && <p className="mt-2 text-sm text-gray-500 whitespace-pre-line">{data.description}</p>}
            {data.location && <PublicBookingLocation location={data.location} />}

            {data.slots.length === 0 ? (
              <p className="mt-6 rounded-lg bg-gray-100 px-4 py-3 text-center text-sm text-gray-600">
                No open times in the next two weeks.
              </p>
            ) : (
              <>
                {shownZoneLabel && (
                  <p className="mt-6 text-xs text-gray-500" data-testid="tz-label">
                    {t(onViewerClock ? 'booking.public.timesShownInMine' : 'booking.public.timesShownIn', { zone: shownZoneLabel })}
                    {zone.mine && (
                      <button type="button" onClick={switchZone} className="ml-2 font-medium text-gray-700 underline" data-testid="tz-switch">
                        {zone.showingMine
                          ? t('booking.public.showMerchantZone', { zone: data.timeZoneLabel ?? '' })
                          : t('booking.public.showMyZone', { zone: zone.mine.label })}
                      </button>
                    )}
                  </p>
                )}
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {days.map(([key, iso]) => (
                    <button key={key} type="button"
                      onClick={() => { setSelectedDay(key); setSelectedSlot(null); }}
                      className={`shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium ${
                        selectedDay === key ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                      }`}>
                      {dayLabel(iso, tz)}
                    </button>
                  ))}
                </div>

                <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {daySlots.map((s) => (
                    <button key={s.start} type="button"
                      onClick={() => setSelectedSlot(s.start)}
                      className={`rounded-lg border px-2 py-2 text-sm tabular-nums ${
                        selectedSlot === s.start ? 'border-gray-900 bg-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:border-gray-400'
                      }`}>
                      {timeLabel(s.start, tz)}
                    </button>
                  ))}
                </div>

                <div className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="bk-name" className="text-sm font-medium text-gray-700">Name</label>
                    <input id="bk-name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="bk-email" className="text-sm font-medium text-gray-700">Email <span className="text-red-500">*</span></label>
                    <input id="bk-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none" />
                  </div>
                  <div>
                    <label htmlFor="bk-phone" className="text-sm font-medium text-gray-700">
                      {phoneRequired ? t('booking.public.phoneRequiredLabel') : t('booking.public.phone')}
                      {phoneRequired && <span className="text-red-500"> *</span>}
                    </label>
                    <input id="bk-phone" type="tel" inputMode="tel" autoComplete="tel" required={phoneRequired} value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder={t('booking.public.phonePlaceholder')}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none" />
                    <p className="mt-1 text-xs text-gray-400">{phoneRequired ? t('booking.public.phoneRequiredHint') : t('booking.public.phoneHint')}</p>
                  </div>
                </div>

                {(data.questions ?? []).length > 0 && (
                  <div className="mt-4 space-y-4">
                    {(data.questions ?? []).map((q) => (
                      <div key={q.key}>
                        <label htmlFor={`bk-q-${q.key}`} className="text-sm font-medium text-gray-700">
                          {q.label}
                          {q.required && <span className="text-red-500"> *</span>}
                        </label>
                        {q.type === 'choice' ? (
                          <select
                            id={`bk-q-${q.key}`}
                            required={q.required}
                            value={answers[q.key] ?? ''}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.key]: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
                          >
                            {/* Empty first option so a required choice is a real
                                choice — otherwise the first one is pre-answered
                                for the booker and every reply reads the same. */}
                            <option value="">{t('booking.questions.selectPlaceholder')}</option>
                            {(q.options ?? []).map((o) => (
                              <option key={o} value={o}>{o}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`bk-q-${q.key}`}
                            type={q.type === 'phone' ? 'tel' : q.type === 'email' ? 'email' : 'text'}
                            required={q.required}
                            value={answers[q.key] ?? ''}
                            onChange={(e) => setAnswers((prev) => ({ ...prev, [q.key]: e.target.value }))}
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Honeypot */}
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden"
                  value={website} onChange={(e) => setWebsite(e.target.value)} />

                {submitError && <p className="mt-4 text-sm text-amber-700">{submitError}</p>}

                {/* The accent carries to the one button that matters. Falls
                    back to the neutral dark when the page has no colour, so an
                    unbranded page is pixel-identical to what it was. */}
                <button type="submit" disabled={submitting || !selectedSlot}
                  data-testid="brand-submit"
                  data-cookie-avoid
                  {...(accent !== null ? { style: { backgroundColor: accent } } : {})}
                  className={`mt-6 w-full rounded-lg px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50 ${accent === null ? 'bg-gray-900 hover:bg-gray-800' : 'hover:opacity-90'}`}>
                  {submitting ? 'Booking…' : selectedSlot ? `Book ${timeLabel(selectedSlot, tz)}` : 'Pick a time'}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
